import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { selectPhotoSphereAnchoringComplete, selectReportPageTitle } from '../../redux/slices/reportSlice/report';
import { useAppSelector } from '../../redux/store';
import { LocalStorageKey } from '../../vendor/local';
import ErrorBoundary from '../error/ErrorBoundary';
import ReportErrorBoundary from '../error/ReportErrorBoundary';
import ReportFooter from './AppFooter';
import ReportDrawerContent from './Drawer/ReportDrawerContent';
import ReportDrawerFooter from './Drawer/ReportDrawerFooter';
import DownloadReportsButton from './Header/DownloadReportsButton';
import { useLayoutContext } from './useLayoutContext';
import { ReportLayoutProvider } from './useReportLayoutContext';
import { Helmet } from 'react-helmet-async';
import useReportId from "../../utils/report/useReportId";
import {
  Alert,
  AlertTitle,
  Box,
  Container,
  Link,
  Stack,
  Theme,
  Toolbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import MainAppBar from "./Header/MainAppBar";
import { DRAWER_WIDTH } from "./Drawer/MainDrawer";
import { Drawer } from "./Drawer";
import { Campaign, OpenInNewOutlined } from '@mui/icons-material';

const TARGET_HOST = (() => {
  let target = 'localhost:8090';
  const host = window.location.host;
  if (host.includes('app')) target = host.replace('app', 'legacy');//'legacy.inspectify.com';
  if (host.includes('legacy')) target = host.replace('legacy', 'app');//'app.inspectify.com';
  return window.location.href.replace(host, target);
})();

const ReportLayout = () => {
  const { orderToken } = useParams();
  const {
    setDrawerContent,
    setSearchOptions,
    setDrawerFooter,
    setDrawerVisible,
    setAppBarTools,
    setPrintMode,
    drawerVisible,
    drawerOpen,
    printMode,
  } = useLayoutContext();
  const reportId = useReportId();
  const pageTitle = useAppSelector(selectReportPageTitle);
  const photoSphereAnchoringComplete = useAppSelector(selectPhotoSphereAnchoringComplete);
  const [alertNewVersionVisible, setAlertNewVersionVisible] = useState(true);

  const [drawerWidth, setDrawerWidth] = useState(DRAWER_WIDTH);
  const matchUpLG = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  useEffect(() => {
    if (printMode) {
      setDrawerVisible(false);
      setSearchOptions({ visible: false, placeholder: '', onSearch: undefined });
    } else {
      setDrawerVisible(!!reportId);
      setDrawerContent(<ReportDrawerContent />);
      setDrawerFooter(<ReportDrawerFooter />);
      setAppBarTools(<DownloadReportsButton />);
    }
  }, [reportId, setDrawerVisible, setPrintMode, setSearchOptions, setDrawerContent, setDrawerFooter, setAppBarTools]);

  useEffect(() => {
    setDrawerVisible(drawerVisible);
  }, [drawerVisible, setDrawerVisible]);

  useEffect(() => {
    // adjust drawer width based on visibility and screen size
    setDrawerWidth(
      matchUpLG
        ? drawerVisible && drawerOpen ? DRAWER_WIDTH : 0
        : 0,
    );
  }, [drawerVisible, drawerOpen, setDrawerWidth, matchUpLG]);

  // NOTE phil: to make sure the orderToken is available for all queries
  useLayoutEffect(() => {
    localStorage.setItem(LocalStorageKey.OrderToken, orderToken || '');
    return () => {
      localStorage.removeItem(LocalStorageKey.OrderToken);
    };
  }, [orderToken]);

  useEffect(() => {
    if (window.location.host.includes('pro')) {
      setAlertNewVersionVisible(false);
    }
  }, []);

  return (
    <ErrorBoundary fallback={ReportErrorBoundary}>
      <Helmet>
        <title>Inspectify - {pageTitle}</title>
        <meta
          name="description"
          content="Request Home Inspection and access Home Inspection Report"
        />
      </Helmet>
      <ReportLayoutProvider>
        <MainAppBar width={drawerWidth} />
        {drawerVisible && (
          <Box
            component="nav"
            sx={{ width: drawerWidth }}
            aria-label="dashboard menu"
          >
            <Drawer />
          </Box>
        )}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
          }}
        >
          <Toolbar sx={{ mb: 2 }} />
          <Container maxWidth={printMode ? false : 'lg'} sx={{ mt: 1 }}>
            <Stack spacing={2} mb={2}>
              {alertNewVersionVisible && !printMode && (
                <Alert severity="error" color="warning" onClose={() => setAlertNewVersionVisible(false)}>
                  <AlertTitle>
                    A New Version of Our Web Report is Here!
                  </AlertTitle>
                  <Typography>
                    We’ve updated our web report to bring you a better, faster, and more user-friendly experience
                  </Typography>
                  <Typography>
                    Having any issue? Go back the <Link
                    href={TARGET_HOST}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    original version <OpenInNewOutlined sx={{ fontSize: 12 }} />
                  </Link>
                  </Typography>
                </Alert>
              )}

              {!photoSphereAnchoringComplete && !printMode && (
                <Alert color="warning" icon={<Campaign />}>
                  <AlertTitle>
                    Your Floor Plan Is Being Processed
                  </AlertTitle>
                  <Typography>
                    We are currently processing the floor plan for this property. It will be available in the report
                    within
                    24
                    hours. You'll receive an email notification as soon as it's ready for viewing.
                  </Typography>
                </Alert>
              )}
            </Stack>

            <Outlet />
          </Container>
          <Box pt={2}>
            <ReportFooter />
          </Box>
        </Box>
      </ReportLayoutProvider>
    </ErrorBoundary>
  );
};

export default ReportLayout;
